import { Card, Typography, useTheme, CardContent, Box, Tooltip } from "@mui/material";
import CategoryIcon from "features/News/components/CategoryIcon";
import { useLocation, useParams } from "react-router-dom";
import { ImInfo } from "react-icons/im";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import { categoryDescriptions } from "data/category-description";
import PeerReviewedDetail from "features/News/components/peer-reviewed-detail";
import React from "react";

function TooltipContent({ category, isPeerReviewed }) {
  if (isPeerReviewed){
    return (
      <Typography
        variant="body1"
        component="div"
        dangerouslySetInnerHTML={{
          __html: categoryDescriptions["peer-reviewed"],
        }}
      />
    );


  }
    return (
      <Typography variant="body1">{categoryDescriptions[category]}</Typography>
    );
}

export const SidebarCard = ({
  category,
  categoryType,
  id,
  resultCount = 0,
  totalResults,
  showResults = false,
}) => {
  const theme = useTheme();
  

  const { academiaSearch } = useParams();
  const isMobile = useIsMobile();
  const {pathname} = useLocation();
  const isPeerReviewed = pathname.includes("/peer-reviewed");
  const isPublishersPage = pathname.includes("/partner-publishers");
  const isIndividualCategoryPage = categoryType === null;
  const hideInfoIcon =
    isPublishersPage ||
    categoryType === null ||
    categoryType === "books" ||
    categoryType === "wiki" ||
    categoryType === "peer-reviewed";

  const getLabel = () => {
   let label;

   switch (categoryType) {
     case null:
       label = category;
       break;
     case "wiki":
       label = "Encyclopedia & Dictionary";
       break;
     case "peer-reviewed":
       label = "Peer Reviewed Sources";
       break;
     default:
       label = "Books";
   }


    if (academiaSearch) {
      label = academiaSearch;
    }
    return label;
  };

  const categoryDescription = isIndividualCategoryPage
    ? categoryDescriptions[getLabel()]
    : null;

  const handleInfoClick = () => {};

  const hasExtraContent = isPeerReviewed || Boolean(categoryDescription);
  // 200px roughly covers the navbar, search/filter row, and the
  // "Related Searches" row above the sidebar, so the card takes up
  // as much of the remaining viewport as it can before scrolling.
  const scrollableContentSx = hasExtraContent
    ? {
        maxHeight: { xs: "30vh", sm: "45vh", lg: "calc(100vh - 200px)" },
        overflowY: "auto",
      }
    : {};

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        backgroundColor: theme.palette.secondary.main,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        wordBreak: "break-word",
        minHeight: {
          xs: "20vh",
          sm: categoryType === "peer-reviewed" ? "25vh" : "30vh",
        },
      }}
      id={id}
    >
      <CategoryIcon
        iconId={category}
        sx={{
          fill: theme.palette.secondary.dark,
          position: "absolute",
          fontSize: "30rem",
          right: "-10rem",
          bottom: "-10rem",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 4.4,
          right: 5.4,
          display: "flex",
          zIndex: 10,
          "& > svg": {
            color: theme.palette.secondary.dark,
          },
          "&:hover > svg": {
            color: theme.palette.primary.dark,
          },
        }}
      >
        <Tooltip
          title={
            !isMobile && !hideInfoIcon ? (
              <TooltipContent
                isPeerReviewed={isPeerReviewed}
                category={getLabel()}
              />
            ) : null
          }
          placement="right"
          arrow
        >
          <Box>
            {!hideInfoIcon &&
            <ImInfo
              onClick={handleInfoClick}
              size={16}
              style={{
                p: 1,
              }}
            />
    }
          </Box>
        </Tooltip>
      </Box>
      <CardContent
        sx={{ position: "relative", zIndex: 1, ...scrollableContentSx }}
      >
        <Typography
          component="div"
          key={category}
          fontSize="2.3rem"
          color="primary.dark"
          fontFamily={theme.typography.h3.fontFamily}
          fontWeight={theme.typography.h3.fontWeight}
        >
          {getLabel()}
          {isPeerReviewed && (
            <Typography mt={1} variant="body2" color="primary.dark">
              These publishers peer review all their <b>journals</b>, however,
              your search results are <b>not guaranteed</b> to be peer-reviewed.
              Your results may include Editorials, Commentaries, News, and Book
              Reviews which are <b>not</b> peer-reviewed. Review your results
              carefully.
            </Typography>
          )}
          {categoryDescription && (
            <Typography
              mt={1}
              variant="body2"
              color="primary.dark"
              dangerouslySetInnerHTML={{ __html: categoryDescription }}
            />
          )}
          {showResults && Number.isFinite(totalResults) && (
            <Typography mt={1} variant="body2" color="primary.dark">
              Showing {Math.min(resultCount, totalResults).toLocaleString("en-US")} of {totalResults.toLocaleString("en-US")} results
            </Typography>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};
